import React, { useState, useEffect, useRef } from 'react';
import { CurrentTrialsSection } from '../../components/activity/CurrentTrialsSection';
import { TopStudentsSection } from '../../components/activity/TopStudentsSection';
import { GoogleReviewsSection } from '../../components/activity/GoogleReviewsSection';
import { WebsiteStatusSection } from '../../components/activity/WebsiteStatusSection';
import { BusinessSetupModal } from '../../components/activity/BusinessSetupModal';
import { AlertCard } from '../../components/dashboard/AlertCard';
import { useDashboardAlerts } from '../../hooks/useDashboardAlerts';
import { useCurrentCompany } from '../../hooks/useCurrentCompany';
import { useGoogleBusinessSummary } from '../../hooks/useGoogleBusinessSummary';
import { supabase } from '../../lib/supabase';
import { TrendingUp, Star, Globe, Users } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { FeatureRequestButton } from '../../components/activity/FeatureRequestButton';

export default function ActivityPage() {
  const { data: alerts = [], isLoading: alertsLoading } = useDashboardAlerts();
  const { data: companyId } = useCurrentCompany();
  const { data: businessSummary } = useGoogleBusinessSummary();
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  
  const trialsRef = useRef<HTMLDivElement>(null);
  const studentsRef = useRef<HTMLDivElement>(null);
  const reviewsRef = useRef<HTMLDivElement>(null);
  const websiteRef = useRef<HTMLDivElement>(null);

  // Check if setup modal should be shown (daily for first-time visitors)
  useEffect(() => {
    const checkSetupModal = async () => {
      if (!companyId) return;

      // Check if both Google and Website are configured
      // Check businessSummary first, then fallback to place_id (in case Edge Function failed)
      let hasGoogle = !!businessSummary;
      if (!hasGoogle) {
        try {
          const { data: companyData } = await (supabase as any)
            .from('companies')
            .select('place_id')
            .eq('id', companyId)
            .single();
          hasGoogle = !!(companyData?.place_id);
        } catch {
          hasGoogle = false;
        }
      }
      
      let hasWebsite = false;
      try {
        const { data, error } = await (supabase as any)
          .from('companies')
          .select('website_url')
          .eq('id', companyId)
          .single();
        
        if (!error && data?.website_url) {
          hasWebsite = true;
        }
      } catch {
        // Ignore errors
      }

      // If both are configured, don't show modal
      if (hasGoogle && hasWebsite) {
        return;
      }

      // Check localStorage for last shown date
      const storageKey = `business-setup-modal-${companyId}`;
      const lastShown = localStorage.getItem(storageKey);
      const today = new Date().toDateString();

      // Show modal if:
      // 1. Never shown before, OR
      // 2. Last shown was not today (daily reminder)
      if (!lastShown || lastShown !== today) {
        setShowSetupModal(true);
      }
    };

    checkSetupModal();
  }, [companyId, businessSummary]);

  const handleCloseSetupModal = () => {
    if (companyId) {
      const storageKey = `business-setup-modal-${companyId}`;
      const today = new Date().toDateString();
      localStorage.setItem(storageKey, today);
    }
    setShowSetupModal(false);
  };

  const scrollToSection = (section: 'trials' | 'students' | 'reviews' | 'website') => {
    setActiveSection(section);
    const refs = {
      trials: trialsRef,
      students: studentsRef,
      reviews: reviewsRef,
      website: websiteRef,
    };
    refs[section].current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setTimeout(() => setActiveSection(null), 1000);
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Page Header - matches Billing & Subscriptions styling */}
      <div className="border-b border-white/10 bg-[#111010] pb-3 md:pb-4 shrink-0">
        <h1 className="text-text-primary font-heading text-2xl md:text-4xl font-bold">RollOps Pulse</h1>
        <p className="text-white/80 mt-1">Monitor student progress, reviews, and business health</p>
      </div>

      {/* Content - full height, scrollable */}
      <div className="flex-1 bg-[#111010] overflow-auto min-h-0 min-w-0 py-6">
        {/* Quick Navigation */}
        <div className="mb-6 flex flex-wrap gap-3">
          <Button
            onClick={() => scrollToSection('trials')}
            className={`bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE] ${activeSection === 'trials' ? 'ring-2 ring-[#9B1421]' : ''}`}
            size="sm"
          >
            <Users className="w-4 h-4 mr-2" />
            Current Trials
          </Button>
          <Button
            onClick={() => scrollToSection('students')}
            className={`bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE] ${activeSection === 'students' ? 'ring-2 ring-[#9B1421]' : ''}`}
            size="sm"
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            Top Students
          </Button>
          <Button
            onClick={() => scrollToSection('reviews')}
            className={`bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE] ${activeSection === 'reviews' ? 'ring-2 ring-[#9B1421]' : ''}`}
            size="sm"
          >
            <Star className="w-4 h-4 mr-2" />
            Google Reviews
          </Button>
          <Button
            onClick={() => scrollToSection('website')}
            className={`bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE] ${activeSection === 'website' ? 'ring-2 ring-[#9B1421]' : ''}`}
            size="sm"
          >
            <Globe className="w-4 h-4 mr-2" />
            Website Assessment
          </Button>
        </div>

        {/* Urgent Actions Section */}
        {!alertsLoading && alerts.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-[#EBFFEE] mb-4">Urgent Actions</h2>
            <AlertCard alerts={alerts} maxAlerts={10} />
          </div>
        )}

        {/* Current Trials Section - Above Top Students (new students are more important) */}
        <div ref={trialsRef} className="mb-8 scroll-mt-4">
          <CurrentTrialsSection />
        </div>

        {/* Top Students Section */}
        <div ref={studentsRef} className="mb-8 scroll-mt-4">
          <TopStudentsSection />
        </div>

        {/* Google Reviews Section */}
        <div ref={reviewsRef} className="mb-8 scroll-mt-4">
          <GoogleReviewsSection />
        </div>

        {/* Website Status Section */}
        <div ref={websiteRef} className="mb-8 scroll-mt-4">
          <WebsiteStatusSection />
        </div>

        {/* Additional Assessment Services */}
        <div className="mt-12 pt-8 border-t border-[#504A4A]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Social Account Assessment Request */}
            <div className="p-6 bg-[#221F1F] border border-[#504A4A] rounded-lg">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-[#363232] rounded-lg border border-[#504A4A]">
                  <svg className="w-6 h-6 text-text-primary" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-[#EBFFEE] mb-2">Social Media Assessment</h3>
                  <p className="text-sm text-white/70 mb-4">
                    Get insights on your social media presence, engagement, and opportunities to grow your online community.
                  </p>
                  <FeatureRequestButton
                    featureCategory="social_assessment"
                    featureName="Social Media Account Assessment"
                    description="Comprehensive analysis of your social media accounts including engagement metrics, content performance, and growth opportunities"
                  />
                </div>
              </div>
            </div>

            {/* Business Review Request */}
            <div className="p-6 bg-[#221F1F] border border-[#504A4A] rounded-lg">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-[#363232] rounded-lg border border-[#504A4A]">
                  <TrendingUp className="w-6 h-6 text-text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-[#EBFFEE] mb-2">Business Review</h3>
                  <p className="text-sm text-white/70 mb-4">
                    Schedule a free business review with our team. We&apos;ll understand your goals, discuss your needs, and explore options 
                    for managing different aspects of your gym - including phone calls if needed. Eventually, our AI will recommend 
                    the best additional RollOps services you might be interested in.
                  </p>
                  <Button
                    onClick={() => window.location.href = 'mailto:support@rollops.pro?subject=Business Review Request'}
                    className="bg-[#9B1421] hover:bg-[#7A0F1A] text-white text-sm"
                  >
                    Request Business Review
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Business Setup Modal */}
      {showSetupModal && (
        <BusinessSetupModal onClose={handleCloseSetupModal} />
      )}
      </div>
  );
}
